import { useParams, Link } from 'react-router-dom';
import { getComicById } from '@/mocks/comics';
import SEO from '@/components/base/SEO';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import SchemaOrg from '@/components/base/SchemaOrg';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const categoryNameMap: Record<string, string> = {
  love: '恋爱', action: '动作', fantasy: '奇幻', thriller: '悬疑',
  comedy: '喜剧', drama: '剧情', school: '校园', bl: '耽美', horror: '恐怖',
};

export default function ComicDetailPage() {
  const { id } = useParams<{ id: string }>();
  const comic = getComicById(Number(id));

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center py-16">
            <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
              <i className="ri-file-unknow-line text-3xl"></i>
            </div>
            <h1 className="font-heading text-xl font-bold text-foreground-800 mb-2">漫画未找到</h1>
            <p className="text-foreground-500 text-sm">抱歉，您要找的漫画不存在或已被移除。</p>
            <Link to="/" className="inline-block mt-6 px-6 py-2.5 rounded-full bg-primary-500 text-white text-sm hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const statusLabel = comic.status === 'ongoing' ? '连载中' : '已完结';
  const statusClass = comic.status === 'ongoing'
    ? 'bg-accent-500 text-background-50'
    : 'bg-secondary-500 text-background-50';

  const articleSchema = {
    headline: `${comic.title} - 韩国漫画`,
    description: comic.description,
    image: comic.cover,
    author: { '@type': 'Person', name: comic.author },
    datePublished: '2026-01-01',
    dateModified: comic.updateDate,
    publisher: { '@type': 'Organization', name: '可游漫画网' },
  };

  return (
    <>
      <SEO
        title={comic.title}
        description={`${comic.title} - ${comic.author}作品。${comic.description.substring(0, 120)}。可游漫画网提供韩国漫画${comic.title}免费在线阅读，无删减下拉式体验。`}
        keywords={`韩国漫画,${comic.title},${comic.author},韩漫在线免费阅读,可游漫画网,无删减漫画`}
        canonicalPath={`/comic/${comic.id}`}
        image={comic.cover}
        type="article"
      />
      <SchemaOrg type="Article" data={articleSchema} />
      <ScrollToTop />

      <Navbar />

      <main className="pt-16">
        {/* Comic Header */}
        <section className="bg-background-100 py-8 md:py-12 px-4 md:px-6">
          <div className="max-w-[1440px] mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '漫画分类', href: '/category/love' },
                { label: comic.title },
              ]}
            />

            <div className="flex flex-col md:flex-row gap-6 md:gap-8 mt-4">
              {/* Cover */}
              <div className="flex-shrink-0 w-full sm:w-[200px] md:w-[240px] mx-auto md:mx-0">
                <div className="aspect-[3/4] rounded-xl overflow-hidden border border-background-200/70">
                  <img
                    src={comic.cover}
                    alt={`${comic.title} - 韩国漫画封面`}
                    title={`${comic.title} - 韩国漫画在线免费阅读`}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusClass}`}>
                    {statusLabel}
                  </span>
                  {comic.categories.map((catSlug) => (
                    <Link
                      key={catSlug}
                      to={`/category/${catSlug}`}
                      className="px-3 py-1 rounded-full text-xs font-medium bg-secondary-100 text-secondary-700 hover:bg-secondary-200 transition-colors duration-200 whitespace-nowrap"
                    >
                      {categoryNameMap[catSlug] || catSlug}
                    </Link>
                  ))}
                </div>

                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-800">
                  {comic.title}
                </h1>
                <p className="text-sm text-foreground-500 mt-1">作者：{comic.author}</p>

                {/* Stats */}
                <div className="flex items-center gap-4 md:gap-6 mt-4">
                  <div className="flex items-center gap-1.5">
                    <i className="ri-star-fill text-accent-500"></i>
                    <span className="text-sm font-semibold text-foreground-800">{comic.rating}</span>
                    <span className="text-xs text-foreground-500">/ 5.0</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <i className="ri-eye-line text-foreground-400"></i>
                    <span className="text-sm text-foreground-600">
                      {comic.views > 10000 ? `${(comic.views / 10000).toFixed(0)}万` : comic.views} 次阅读
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <i className="ri-file-list-3-line text-foreground-400"></i>
                    <span className="text-sm text-foreground-600">{comic.chapters.length} 话</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <i className="ri-calendar-line text-foreground-400"></i>
                    <span className="text-sm text-foreground-600">更新于 {comic.updateDate}</span>
                  </div>
                </div>

                {/* Description */}
                <div className="mt-5 p-4 md:p-5 rounded-xl bg-white border border-background-200/70">
                  <h3 className="font-heading text-sm font-semibold text-foreground-800 mb-2">作品简介</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{comic.description}</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Chapters */}
        <section className="py-8 md:py-10 px-4 md:px-6">
          <div className="max-w-[1440px] mx-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-800">
                <i className="ri-list-check-2 mr-2 text-primary-500"></i>
                章节列表
              </h2>
              <div className="flex items-center gap-2">
                <button className="px-3 py-1.5 rounded-lg text-xs font-medium bg-background-100 text-foreground-600 hover:bg-background-200 transition-colors duration-200 whitespace-nowrap cursor-pointer">
                  <i className="ri-sort-desc mr-1"></i>最新在前
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 md:gap-3">
              {comic.chapters.map((chapter) => (
                <a
                  key={chapter.id}
                  href={`#chapter-${chapter.number}`}
                  className="flex items-center justify-between px-4 py-3 rounded-lg bg-white border border-background-200/70 hover:border-primary-200 hover:bg-primary-50/30 transition-all duration-200 cursor-pointer"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-100 text-foreground-600 text-xs font-medium flex-shrink-0">
                      {chapter.number}
                    </div>
                    <span className="text-sm text-foreground-700 truncate">{chapter.title}</span>
                  </div>
                  <span className="text-xs text-foreground-400 flex-shrink-0 ml-2">{chapter.date}</span>
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* Related / More Comics */}
        <section className="py-8 md:py-10 px-4 md:px-6 bg-background-100">
          <div className="max-w-[1440px] mx-auto">
            <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-800 mb-6">
              猜你喜欢
            </h2>
            <div className="flex flex-wrap gap-3">
              {comic.categories.map((catSlug) => (
                <Link
                  key={catSlug}
                  to={`/category/${catSlug}`}
                  className="px-5 py-2.5 rounded-full bg-white border border-background-200/70 text-sm text-foreground-700 hover:text-primary-500 hover:border-primary-200 transition-all duration-200 whitespace-nowrap"
                >
                  浏览更多{categoryNameMap[catSlug] || catSlug}漫画 <i className="ri-arrow-right-line ml-1"></i>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}